import type { BulkDraft } from '~/api/data/email-bulk-send/queries';
import type { EmailBulkSendRecipientInput } from '~/api/graphql';
import { CsvImport, type CsvSchema, type DiffChange } from '~/components/csv-import';
import { pluralize } from '~/lib/pluralize';

const EMAIL_KEY = 'email';
const NAME_KEY = 'name';

/** An Email column, which every file needs, and a Name column, which it may have. */
export const BULK_DRAFT_CSV_SCHEMA: CsvSchema = {
  fields: [
    { key: EMAIL_KEY, label: 'Email', type: 'email', required: true },
    { key: NAME_KEY, label: 'Name', type: 'string' }
  ],
  keyColumn: EMAIL_KEY
};

const recipientFromRow = (row: DiffChange): EmailBulkSendRecipientInput => ({
  email: row.values?.[EMAIL_KEY]?.trim() ?? '',
  name: row.values?.[NAME_KEY]?.trim() || undefined
});

/**
 * Upload CSV: previews the file against the list, then adds the rows the agent keeps. A
 * client already on the list is left as it is, whatever name the file gives them.
 */
export const BulkDraftCsvImport = ({
  recipients,
  roomLeft,
  addRecipients
}: {
  recipients: BulkDraft['recipients'];
  /** How many more recipients the bulk draft can take. */
  roomLeft: number;
  /** Adds the clients; resolves to why it couldn't, or to undefined once they're added. */
  addRecipients: (recipients: EmailBulkSendRecipientInput[]) => Promise<string | undefined>;
}) => (
  <div className="rounded-lg bg-white p-4 shadow dark:bg-neutral-800">
    <CsvImport
      listName="Recipients"
      schema={BULK_DRAFT_CSV_SCHEMA}
      currentData={recipients.map((recipient) => ({
        [EMAIL_KEY]: recipient.email ?? '',
        [NAME_KEY]: recipient.name ?? ''
      }))}
      addOnly
      maxAdded={roomLeft}
      upToDateMessage="Everyone in this file is already in the list."
      commitLabel={(count) => `Add ${count} ${pluralize(count, 'recipient')}`}
      onCommit={async ({ added }) => {
        const failure = await addRecipients(added.map(recipientFromRow));
        return failure ? { status: 'failed', error: failure } : { status: 'applied' };
      }}
    />
  </div>
);
